/**
 * Display helpers for workflow status history entries.
 */

import type { NoteAssigneeResponse } from "../../../api/clinicalTrialsApiTypes";

const CLINIC_TIME_ZONE = "America/New_York";

/**
 * Format an ISO timestamp as `12:20PM | 09/12/25` in the clinic timezone.
 *
 * @param iso - Server `changed_at` value
 * @returns Formatted time and short date for the timeline header
 */
export function formatStatusHistoryTimestamp(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  const time = date
    .toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: CLINIC_TIME_ZONE,
    })
    .replace(/\s/g, "")
    .toUpperCase();
  const datePart = date.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    timeZone: CLINIC_TIME_ZONE,
  });
  return `${time} | ${datePart}`;
}

/**
 * Render a human-readable actor label for the timeline header.
 *
 * @param changedBy - User id or service label from the API
 * @param assignees - Facility members used to resolve user ids to names
 * @returns Short label suitable for the timeline
 */
export function formatStatusHistoryActor(
  changedBy: string,
  assignees: NoteAssigneeResponse[] = [],
): string {
  const trimmed = changedBy.trim();
  if (!trimmed) return "Unknown";
  const assignee = assignees.find((member) => member.user_id === trimmed);
  if (assignee) {
    return (
      assignee.display_name?.trim() || assignee.email?.trim() || "Team member"
    );
  }
  if (trimmed === "dev-local") return "Dev Local";
  if (trimmed.includes("@")) {
    return trimmed.split("@")[0] ?? trimmed;
  }
  if (trimmed.length > 24) {
    return "Team member";
  }
  return trimmed;
}
